import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { MinMax, Range } from "@nova/protobuf"
import { InspectorPanelAccordion } from "../accordion/InspectorPanelAccordion"
import { InspectorPanelAccordionKeyValueToggle } from "../accordion/InspectorPanelAccordionKeyValueToggle"
import { selectShowLayerByIdMapForHotspotOrthoOverlaps } from "features/map/layers/selectors"
import { activateFlightPath, deactivateFlightPath } from "features/map/layers/slice"
import { useDebounceDispatch } from "tools/store"
import {
    ColorSwatchCategory,
    hexColorSwatches,
    HotspotPointTypes,
    JobStatusType,
    OrthoPalettes,
    ToolName,
} from "@nova/constants"
import { InspectorPanelAccordionSubsections } from "../accordion/InspectorPanelAccordionSubsections"
import { InspectorPanelAccordionRadioButton } from "../accordion/InspectorPanelAccordionRadioButton"
import { config } from "@nova/configuration/browser"
import { ButtonSecondary, ButtonTertiary, ButtonStatus } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { UserPlacedHotspots } from "plugins/InfraredImage/HotspotMenu/UserPlacedHotspots"
import { InspectorPanelKeyValueDropdown } from "../InspectorPanelKeyValueDropdown"
import { decodeNumberToColor, encodeColorToNumber } from "@nova/serializer/src/client/util"
import { ColorPalette } from "../icons/TODO-put-in-package/ColorPalette"
import classNames from "classnames"
import { OrthoPalette } from "../icons/TODO-put-in-package/OrthoPalette"
import { InspectorPanelAccordionSlider } from "../accordion/InspectorPanelAccordionSlider"
import { selectOrthoOpacityById, setOrthoOpacity } from "plugins/Orthomosaic/OrthomosaicSlice"
import {
    activateOrthoComparison,
    coordinatesToComparisonBounds,
    type OrthoComparisonBounds,
} from "plugins/Orthomosaic/OrthoComparisonSlice"
import { selectHotspotBoundingBoxForFlightId } from "plugins/InfraredImage/InfraredImageSlice"
import { useInspectorPanelHotspots } from "./useInspectorPanelHotspots"
import { InspectorPanelHotspotLayerFilters } from "./filters/InspectorPanelHotspotLayerFilters"
import { useOptimisticLocalState } from "../useOptimisticLocalState"
import { openHotspotMultiRemoval } from "features/data-management/hotspots/slice"
import { AlertContainer } from "@nova/component-alert-container"
import { Hyperlink } from "@nova/component-typography"
import { getErrorObject } from "@nova/pymodule-error-handling"
import { scanAreaFillColorSwatches, scanAreaOutlineColorSwatches } from "../constants"
import { useGlobalTerrainData } from "features/map/GlobalTerrainManager"
import {
    selectInfraredFlightById,
    selectHotspotPointLayerIsVisible,
    selectHotspotPolygonIsVisible,
    selectHotspotLabelLayerIsVisible,
    selectRemovedHotspotsIsVisible,
    selectHotspotOrthoIsVisible,
    selectInfraredFlightPointColorById,
    selectInfraredFlightScanAreaFillColorById,
    selectInfraredFlightScanAreaOutlineColorById,
    selectInfraredFlightPolyStrokeById,
    selectInfraredFlightPolyFillById,
    selectInfraredFlightOrthoGradientById,
    selectFlightPathIsEnabled,
    selectHotspotScanAreaIsEnabled,
} from "features/entity-caching/flights/selectors"
import {
    deactivateHotspotOrthoOverlapLayerAction,
    activateHotspotOrthoOverlapLayerAction,
    deactivateHotspotScanAreaLayerAction,
    activateHotspotScanAreaLayerAction,
    deactivateHotspotPointLayerAction,
    activateHotspotPointLayerAction,
    deactivateHotspotLabelsLayerAction,
    activateHotspotLabelsLayerAction,
    deactivateRemovedHotspotsLayerAction,
    activateRemovedHotspotsLayerAction,
    activateHotspotPolygonLayerAction,
    deactivateHotspotPolygonLayerAction,
    deactivateHotspotPolygonAreaLayerAction,
    deactivateHotspotOrthoLayerAction,
    activateHotspotOrthoLayerAction,
} from "features/entity-caching/flights/slice"
import {
    selectHotspotsByFlightId,
    selectSortedAndFilteredHotspotsByFlightId,
} from "features/entity-caching/hotspots/selectors"
import { selectInfraredImagesAndUploadsByFlightId } from "features/entity-caching/images/hotspots/selectors"
import { updateInfraredFlight } from "features/pubsub/entityActions/flights/infrared"
import { selectOrthoMaskByFlightId } from "features/entity-caching/ortho-masks/selectors"
import { startMaskDrawing } from "plugins/Orthomosaic/OrthoMaskSlice"
import { updateOrthoMask } from "features/pubsub/entityActions/cubes/orthoMask"
import { setActiveTool } from "plugins/Toolbox/tool-registry"

export type InfraredFlightUpdates = Record<
    string,
    | string
    | number
    | boolean
    | {
          range: Range[]
          unit: string
      }
    | {
          perimeter?: boolean
          temperature?: MinMax | undefined
          size?: MinMax | undefined
      }
    | undefined
>

const pointColorSwatches = hexColorSwatches[ColorSwatchCategory.Hotspot].map((hex) =>
    encodeColorToNumber(hex)
)

const polygonOutlineColorSwatches = hexColorSwatches[ColorSwatchCategory.PolygonOutline].map(
    (hex) => encodeColorToNumber(hex)
)
const polygonFillColorSwatches = hexColorSwatches[ColorSwatchCategory.PolygonFill].map((hex) =>
    encodeColorToNumber(hex)
)

const InspectorPanelHotspotLayer = memo(({ id }: { id: string }) => {
    const dispatch = useAppDispatch()
    const { debounceDispatch } = useDebounceDispatch(250)
    const flight = useAppSelector(selectInfraredFlightById(id))
    const allHotspots = useAppSelector(selectHotspotsByFlightId(id))
    const filteredHotspots = useAppSelector(selectSortedAndFilteredHotspotsByFlightId(id))
    const [openCategories, setOpenCategories] = useState(new Set<string>())

    const flightPathIsEnabled = useAppSelector(selectFlightPathIsEnabled(id))
    const orthoOverlapIsEnabled = useAppSelector(selectShowLayerByIdMapForHotspotOrthoOverlaps)[
        id
    ] as boolean | undefined
    const scanAreaIsEnabled = useAppSelector(selectHotspotScanAreaIsEnabled(id))
    const pointsIsEnabled = useAppSelector(selectHotspotPointLayerIsVisible(id))
    const selectedPointAppearance: HotspotPointTypes = flight?.pointType || HotspotPointTypes.Point
    const polygonsAreEnabled = useAppSelector(selectHotspotPolygonIsVisible(id))
    const extraFeaturesJSON = flight?.job?.extraFeatures
    const { useLabelLayer: labelsAreAllowed } = config.use("useLabelLayer")
    const labelsAreEnabled = useAppSelector(selectHotspotLabelLayerIsVisible(id))
    const removedHotspotsEnabled = useAppSelector(selectRemovedHotspotsIsVisible(id))
    const [isPlacingHotspot, setIsPlacingHotspot] = useState(false)
    const orthomosaicIsEnabled = useAppSelector(selectHotspotOrthoIsVisible(id))
    const existingOpacity = useAppSelector(selectOrthoOpacityById(id))
    const orthoMask = useAppSelector(selectOrthoMaskByFlightId(id))
    const uploads = useAppSelector(selectInfraredImagesAndUploadsByFlightId(id))
    const flightBbox = useAppSelector(selectHotspotBoundingBoxForFlightId(id))
    const { terrainState: terrainIsEnabled } = useGlobalTerrainData(["terrainState"])

    // This does NOT refer to categories, but rather individual
    // dropdowns that exist inside of any category.
    const [openDropdownId, setOpenDropdownId] = useState<string>()

    const setCategoryIsOpen = useCallback((category: string, isOpen: boolean) => {
        setOpenCategories((prev) => {
            const openCategories = new Set(prev)
            if (isOpen) openCategories.add(category)
            else openCategories.delete(category)
            return openCategories
        })
    }, [])

    const setHotspotPointType = useCallback(
        (pointType: HotspotPointTypes) => {
            if (!flight) return
            dispatch(updateInfraredFlight({ id: flight.id, updates: { pointType } }))
        },
        [dispatch, flight]
    )

    const { flightPathDistance, formattedOrthoArea, formattedPolygonsArea } =
        useInspectorPanelHotspots(flight)

    const [resizeTrigger, triggerResize] = useState(0)
    useEffect(() => {
        setTimeout(() => triggerResize((prev) => ++prev), 0)
    }, [openDropdownId, flight?.pointType])

    const [pointColor, setPointColor] = useOptimisticLocalState<number>(
        selectInfraredFlightPointColorById(id),
        (pointColor) => {
            if (!flight) return

            const existingRange =
                flight.perimeterDistance?.range &&
                (JSON.parse(JSON.stringify(flight.perimeterDistance.range)) as Range[]).sort(
                    (a, b) => a.distance - b.distance
                )
            if (existingRange?.at(0)) existingRange[0].color = pointColor

            dispatch(
                updateInfraredFlight({
                    id: flight.id,
                    updates: {
                        pointColor,
                        perimeterDistance: flight.perimeterDistance && {
                            ...flight.perimeterDistance,
                            range: existingRange || [],
                        },
                    },
                })
            )
        },
        flight?.pointColor || pointColorSwatches[0]
    )

    const [scanAreaFillColor, setScanAreaFillColor] = useOptimisticLocalState<number>(
        selectInfraredFlightScanAreaFillColorById(id),
        (scanAreaFillColor) => {
            if (!flight) return

            dispatch(
                updateInfraredFlight({
                    id: flight.id,
                    updates: {
                        scanAreaFillColor,
                    },
                })
            )
        },
        flight?.scanAreaFillColor || scanAreaFillColorSwatches[0]
    )
    const [scanAreaOutlineColor, setScanAreaOutlineColor] = useOptimisticLocalState<number>(
        selectInfraredFlightScanAreaOutlineColorById(id),
        (scanAreaOutlineColor) => {
            if (!flight) return

            dispatch(
                updateInfraredFlight({
                    id: flight.id,
                    updates: {
                        scanAreaOutlineColor,
                    },
                })
            )
        },
        flight?.scanAreaOutlineColor || scanAreaOutlineColorSwatches[0]
    )

    const [polygonOutline, setPolygonOutline] = useOptimisticLocalState<number>(
        selectInfraredFlightPolyStrokeById(id),
        (polyStroke) => {
            dispatch(updateInfraredFlight({ id, updates: { polyStroke } }))
        },
        flight?.polyStroke || polygonOutlineColorSwatches[0]
    )

    const [polygonFill, setPolygonFill] = useOptimisticLocalState<number>(
        selectInfraredFlightPolyFillById(id),
        (polyFill) => {
            dispatch(updateInfraredFlight({ id, updates: { polyFill } }))
        },
        flight?.polyFill || polygonFillColorSwatches[0]
    )

    const [palette, setPalette] = useOptimisticLocalState<OrthoPalettes>(
        selectInfraredFlightOrthoGradientById(id),
        (orthoGradient) => {
            dispatch(updateInfraredFlight({ id, updates: { orthoGradient } }))
        },
        flight?.orthoGradient || OrthoPalettes.WhiteHot
    )

    const flightError = useMemo(() => {
        if (!flight?.job?.error) return
        return getErrorObject(flight.job.error)
    }, [flight?.job?.error])

    if (!flight) return null

    const isJobReady = flight.job?.status ? flight.job.status >= JobStatusType.COMPLETED : false

    const handleActivateComparison = () => {
        const bounds: OrthoComparisonBounds | null = flightBbox
            ? {
                  minLng: flightBbox.bottomLeft[0],
                  maxLng: flightBbox.topRight[0],
                  minLat: flightBbox.bottomLeft[1],
                  maxLat: flightBbox.topRight[1],
              }
            : coordinatesToComparisonBounds(flight.orthoBoundingBox)
        if (!bounds) return
        dispatch(
            activateOrthoComparison({
                flightId: flight.id,
                bounds,
                threshold: (bounds.minLng + bounds.maxLng) / 2,
            })
        )
    }

    return (
        <div className="flex flex-col gap-2 mb-2">
            {/* Flight Settings */}
            {uploads && uploads.length > 0 && !flight.skippedProcessing && (
                <InspectorPanelAccordion
                    title="Flight"
                    subtitle={flightPathDistance}
                    isOpen={openCategories.has("flight")}
                    setIsOpen={(isOpen) => setCategoryIsOpen("flight", isOpen)}
                    extraResizeDependency={triggerResize}
                    isToggled={flightPathIsEnabled && flight.visible}
                    setIsToggled={() => {
                        dispatch(
                            flightPathIsEnabled && flight.visible
                                ? deactivateFlightPath({ flightId: flight.id })
                                : activateFlightPath({ flightId: flight.id })
                        )
                        setCategoryIsOpen("flight", !flightPathIsEnabled)

                        if (!flight.visible)
                            dispatch(
                                updateInfraredFlight({
                                    id: flight.id,
                                    updates: { visible: true },
                                })
                            )
                    }}
                >
                    {orthoOverlapIsEnabled !== undefined && (
                        <InspectorPanelAccordionKeyValueToggle
                            keyTitle="Overlap"
                            isToggled={orthoOverlapIsEnabled && flight.visible}
                            setIsToggled={() => {
                                dispatch(
                                    orthoOverlapIsEnabled && flight.visible
                                        ? deactivateHotspotOrthoOverlapLayerAction({
                                              id: flight.id,
                                          })
                                        : activateHotspotOrthoOverlapLayerAction({ id: flight.id })
                                )

                                if (!flight.visible)
                                    dispatch(
                                        updateInfraredFlight({
                                            id: flight.id,
                                            updates: { visible: true },
                                        })
                                    )
                            }}
                        />
                    )}

                    {isJobReady && !!extraFeaturesJSON?.includes("scanArea") && (
                        <>
                            <InspectorPanelAccordionKeyValueToggle
                                keyTitle="Scan Area"
                                isToggled={scanAreaIsEnabled && flight.visible}
                                setIsToggled={() => {
                                    dispatch(
                                        scanAreaIsEnabled && flight.visible
                                            ? deactivateHotspotScanAreaLayerAction({
                                                  id: flight.id,
                                              })
                                            : activateHotspotScanAreaLayerAction({
                                                  id: flight.id,
                                              })
                                    )

                                    if (!flight.visible)
                                        dispatch(
                                            updateInfraredFlight({
                                                id: flight.id,
                                                updates: { visible: true },
                                            })
                                        )
                                }}
                            />
                            {/* Outline color */}
                            <InspectorPanelKeyValueDropdown
                                id="scanarea-outline-color"
                                isOpen={openDropdownId === "scanarea-outline-color"}
                                setOpenDropdownId={setOpenDropdownId}
                                keyTitle="Outline"
                                Icon={Icons.BucketColor}
                                iconClasses={classNames({
                                    "text-gray-0": scanAreaIsEnabled && flight.visible,
                                    "text-gray-500": !(scanAreaIsEnabled && flight.visible),
                                })}
                                iconAccentColor={
                                    scanAreaIsEnabled && flight.visible
                                        ? decodeNumberToColor(scanAreaOutlineColor)
                                        : "#000"
                                }
                                disabled={!(scanAreaIsEnabled && flight.visible)}
                                className="ml-6"
                                includeSpacer
                                isInSubSection
                                align="right"
                            >
                                <ColorPalette
                                    swatches={scanAreaOutlineColorSwatches}
                                    selectedColor={scanAreaOutlineColor}
                                    setSelectedColor={setScanAreaOutlineColor}
                                    setOpenDropdownId={setOpenDropdownId}
                                    triggerResize={() => triggerResize((prev) => ++prev)}
                                />
                            </InspectorPanelKeyValueDropdown>

                            {/* Fill color */}
                            <InspectorPanelKeyValueDropdown
                                id="scanarea-fill-color"
                                isOpen={openDropdownId === "scanarea-fill-color"}
                                setOpenDropdownId={setOpenDropdownId}
                                keyTitle="Fill"
                                Icon={Icons.BucketColor}
                                iconClasses={classNames({
                                    "text-gray-0": scanAreaIsEnabled && flight.visible,
                                    "text-gray-500": !(scanAreaIsEnabled && flight.visible),
                                })}
                                iconAccentColor={
                                    scanAreaIsEnabled && flight.visible
                                        ? decodeNumberToColor(scanAreaFillColor)
                                        : "#000"
                                }
                                disabled={!(scanAreaIsEnabled && flight.visible)}
                                className="ml-6"
                                includeSpacer
                                isInSubSection
                                align="right"
                            >
                                <ColorPalette
                                    swatches={scanAreaFillColorSwatches}
                                    selectedColor={scanAreaFillColor}
                                    setSelectedColor={setScanAreaFillColor}
                                    setOpenDropdownId={setOpenDropdownId}
                                    triggerResize={() => triggerResize((prev) => ++prev)}
                                />
                            </InspectorPanelKeyValueDropdown>
                        </>
                    )}
                </InspectorPanelAccordion>
            )}

            {(isJobReady || (flight.skippedProcessing && allHotspots.length > 0)) && (
                <>
                    <InspectorPanelHotspotLayerFilters
                        id={id}
                        openCategories={openCategories}
                        setCategoryIsOpen={setCategoryIsOpen}
                    />

                    <InspectorPanelAccordion
                        title="Hotspots"
                        subtitle={`${filteredHotspots.length} hotspot${filteredHotspots.length !== 1 ? "s" : ""}`}
                        isToggled={pointsIsEnabled && flight.visible}
                        setIsToggled={() => {
                            dispatch(
                                pointsIsEnabled && flight.visible
                                    ? deactivateHotspotPointLayerAction({ id: flight.id })
                                    : activateHotspotPointLayerAction({ id: flight.id })
                            )
                            setCategoryIsOpen("points", !pointsIsEnabled)

                            if (!flight.visible)
                                dispatch(
                                    updateInfraredFlight({
                                        id: flight.id,
                                        updates: { visible: true },
                                    })
                                )
                        }}
                        isOpen={openCategories.has("points")}
                        setIsOpen={(isOpen) => setCategoryIsOpen("points", isOpen)}
                        hasSubsections={true}
                        shouldPadChildrenVertically={false}
                        extraVerticalPaddingInRem={0}
                        extraResizeDependency={resizeTrigger}
                    >
                        <InspectorPanelAccordionSubsections>
                            {/* Hotspot Appearance */}
                            <div>
                                <h5 className="text-gray-0 mb-1 select-none">Appearance</h5>
                                {/* Point */}
                                <InspectorPanelAccordionRadioButton
                                    title="Point"
                                    isSelected={selectedPointAppearance === HotspotPointTypes.Point}
                                    setIsSelected={() =>
                                        setHotspotPointType(HotspotPointTypes.Point)
                                    }
                                >
                                    {/* Relative Size */}
                                    {!!extraFeaturesJSON?.includes("hotspotArea") && (
                                        <InspectorPanelAccordionKeyValueToggle
                                            keyTitle="Relative Size"
                                            isToggled={
                                                selectedPointAppearance ===
                                                    HotspotPointTypes.Point &&
                                                !!flight.relativeSizeEnabled
                                            }
                                            setIsToggled={() =>
                                                dispatch(
                                                    updateInfraredFlight({
                                                        id: flight.id,
                                                        updates: {
                                                            relativeSizeEnabled:
                                                                !flight.relativeSizeEnabled,
                                                        },
                                                    })
                                                )
                                            }
                                            isInSubSection={true}
                                            disabled={
                                                selectedPointAppearance !== HotspotPointTypes.Point
                                            }
                                        />
                                    )}

                                    {/* Point Color */}
                                    <InspectorPanelKeyValueDropdown
                                        id="point-color"
                                        isOpen={
                                            openDropdownId === "point-color" &&
                                            selectedPointAppearance === HotspotPointTypes.Point
                                        }
                                        setOpenDropdownId={setOpenDropdownId}
                                        keyTitle="Color"
                                        Icon={Icons.BucketColor}
                                        iconClasses={classNames({
                                            "text-gray-0":
                                                selectedPointAppearance === HotspotPointTypes.Point,
                                            "text-gray-500":
                                                selectedPointAppearance !== HotspotPointTypes.Point,
                                        })}
                                        iconAccentColor={
                                            selectedPointAppearance === HotspotPointTypes.Point
                                                ? decodeNumberToColor(pointColor)
                                                : "#404044" // gray-500
                                        }
                                        isInSubSection
                                        includeSpacer
                                        disabled={
                                            selectedPointAppearance !== HotspotPointTypes.Point
                                        }
                                        align="right"
                                    >
                                        <ColorPalette
                                            swatches={pointColorSwatches}
                                            selectedColor={pointColor}
                                            setSelectedColor={setPointColor}
                                            setOpenDropdownId={setOpenDropdownId}
                                            triggerResize={() => triggerResize((prev) => ++prev)}
                                        />
                                    </InspectorPanelKeyValueDropdown>
                                </InspectorPanelAccordionRadioButton>

                                {/* Heat */}
                                <InspectorPanelAccordionRadioButton
                                    title="Heat"
                                    isSelected={flight.pointType === HotspotPointTypes.Heat}
                                    setIsSelected={() =>
                                        setHotspotPointType(HotspotPointTypes.Heat)
                                    }
                                    disabled={terrainIsEnabled}
                                />

                                {/* Hex */}
                                <InspectorPanelAccordionRadioButton
                                    title="Hex"
                                    isSelected={flight.pointType === HotspotPointTypes.Hex}
                                    setIsSelected={() => setHotspotPointType(HotspotPointTypes.Hex)}
                                >
                                    {/* Flatten */}
                                    {!!extraFeaturesJSON?.includes("hotspotArea") &&
                                        !!extraFeaturesJSON?.includes("hotspotThermal") && (
                                            <InspectorPanelAccordionKeyValueToggle
                                                keyTitle="Flatten"
                                                isToggled={
                                                    selectedPointAppearance ===
                                                        HotspotPointTypes.Hex && !!flight.hexIsFlat
                                                }
                                                setIsToggled={() =>
                                                    dispatch(
                                                        updateInfraredFlight({
                                                            id: flight.id,
                                                            updates: {
                                                                hexIsFlat: !flight.hexIsFlat,
                                                            },
                                                        })
                                                    )
                                                }
                                                isInSubSection
                                                disabled={
                                                    selectedPointAppearance !==
                                                    HotspotPointTypes.Hex
                                                }
                                            />
                                        )}
                                </InspectorPanelAccordionRadioButton>
                            </div>

                            {/* Hotspot Labels */}
                            <div className="flex flex-col gap-2">
                                {labelsAreAllowed && (
                                    <InspectorPanelAccordionKeyValueToggle
                                        keyTitle="Show Labels"
                                        isToggled={labelsAreEnabled}
                                        setIsToggled={() =>
                                            dispatch(
                                                labelsAreEnabled
                                                    ? deactivateHotspotLabelsLayerAction({
                                                          id: flight.id,
                                                      })
                                                    : activateHotspotLabelsLayerAction({
                                                          id: flight.id,
                                                      })
                                            )
                                        }
                                    />
                                )}
                                <InspectorPanelAccordionKeyValueToggle
                                    keyTitle="Show Removed Hotspots"
                                    isToggled={removedHotspotsEnabled}
                                    setIsToggled={() =>
                                        dispatch(
                                            removedHotspotsEnabled
                                                ? deactivateRemovedHotspotsLayerAction({
                                                      id: flight.id,
                                                  })
                                                : activateRemovedHotspotsLayerAction({
                                                      id: flight.id,
                                                  })
                                        )
                                    }
                                />
                            </div>

                            {/* Place Manual Hotspot */}
                            <div className="flex flex-col gap-2">
                                <ButtonSecondary
                                    LeftIcon={Icons.Loupe}
                                    title="Place Hotspot"
                                    className="w-full"
                                    status={
                                        isPlacingHotspot
                                            ? ButtonStatus.Selected
                                            : ButtonStatus.Normal
                                    }
                                    onClick={() => setIsPlacingHotspot(!isPlacingHotspot)}
                                />
                                {isPlacingHotspot && (
                                    <UserPlacedHotspots
                                        flightId={flight.id}
                                        cancelHotspotPlace={() => setIsPlacingHotspot(false)}
                                    />
                                )}

                                <ButtonSecondary
                                    LeftIcon={Icons.RemoveSelection}
                                    title="Remove Hotspots"
                                    className="w-full"
                                    onClick={() =>
                                        dispatch(
                                            openHotspotMultiRemoval({
                                                infraredFlightIds: flight.id,
                                            })
                                        )
                                    }
                                />
                            </div>
                        </InspectorPanelAccordionSubsections>
                    </InspectorPanelAccordion>
                </>
            )}

            {flight.job?.status === JobStatusType.FAILED && (
                <AlertContainer
                    type="error"
                    title={flightError?.message || "Processing Failed"}
                    body={
                        <div className="flex flex-col">
                            <small className="text-gray-0">
                                {flightError?.description ||
                                    "An unknown error occured. Please try again."}
                            </small>
                            <Hyperlink
                                title="Contact us for help"
                                href="mailto:help@novamaps.com"
                            />
                        </div>
                    }
                    className="mx-4"
                />
            )}

            {isJobReady && (
                <>
                    {/* Polygons */}
                    {!!extraFeaturesJSON?.includes("newpoly") && allHotspots.length > 0 && (
                        <InspectorPanelAccordion
                            title="Hot Areas"
                            subtitle={
                                formattedPolygonsArea && `${formattedPolygonsArea} total area`
                            }
                            isToggled={polygonsAreEnabled && flight.visible}
                            setIsToggled={() => {
                                if (!polygonsAreEnabled || !flight.visible) {
                                    dispatch(activateHotspotPolygonLayerAction({ id: flight.id }))
                                } else {
                                    dispatch(deactivateHotspotPolygonLayerAction({ id: flight.id }))
                                    dispatch(
                                        deactivateHotspotPolygonAreaLayerAction({ id: flight.id })
                                    )
                                }

                                setCategoryIsOpen("polygons", !polygonsAreEnabled)

                                if (!flight.visible)
                                    dispatch(
                                        updateInfraredFlight({
                                            id: flight.id,
                                            updates: { visible: true },
                                        })
                                    )
                            }}
                            isOpen={openCategories.has("polygons")}
                            setIsOpen={(isOpen) => setCategoryIsOpen("polygons", isOpen)}
                        >
                            <InspectorPanelKeyValueDropdown
                                id="poly-outline"
                                isOpen={openDropdownId === "poly-outline"}
                                setOpenDropdownId={setOpenDropdownId}
                                keyTitle="Outline"
                                Icon={Icons.BucketColor}
                                iconClasses="text-gray-0"
                                iconAccentColor={decodeNumberToColor(polygonOutline)}
                            >
                                <ColorPalette
                                    swatches={polygonOutlineColorSwatches}
                                    selectedColor={polygonOutline}
                                    setSelectedColor={setPolygonOutline}
                                    setOpenDropdownId={setOpenDropdownId}
                                    triggerResize={() => triggerResize((prev) => ++prev)}
                                />
                            </InspectorPanelKeyValueDropdown>

                            <InspectorPanelKeyValueDropdown
                                id="poly-fill"
                                isOpen={openDropdownId === "poly-fill"}
                                setOpenDropdownId={setOpenDropdownId}
                                keyTitle="Fill"
                                Icon={Icons.BucketColor}
                                iconClasses="text-gray-0"
                                iconAccentColor={decodeNumberToColor(polygonFill)}
                            >
                                <ColorPalette
                                    swatches={polygonFillColorSwatches}
                                    selectedColor={polygonFill}
                                    setSelectedColor={setPolygonFill}
                                    setOpenDropdownId={setOpenDropdownId}
                                    triggerResize={() => triggerResize((prev) => ++prev)}
                                />
                            </InspectorPanelKeyValueDropdown>
                        </InspectorPanelAccordion>
                    )}

                    {/* Orthomosaic */}
                    {!!extraFeaturesJSON?.includes("ortho") && (
                        <InspectorPanelAccordion
                            title="IR Orthomosaic"
                            subtitle={formattedOrthoArea && `${formattedOrthoArea} coverage`}
                            isToggled={orthomosaicIsEnabled && flight.visible}
                            setIsToggled={() => {
                                dispatch(
                                    orthomosaicIsEnabled && flight.visible
                                        ? deactivateHotspotOrthoLayerAction({ id: flight.id })
                                        : activateHotspotOrthoLayerAction({ id: flight.id })
                                )

                                setCategoryIsOpen("ortho", !orthomosaicIsEnabled)

                                if (!flight.visible)
                                    dispatch(
                                        updateInfraredFlight({
                                            id: flight.id,
                                            updates: { visible: true },
                                        })
                                    )
                            }}
                            isOpen={openCategories.has("ortho")}
                            setIsOpen={(isOpen) => setCategoryIsOpen("ortho", isOpen)}
                            hasSubsections
                        >
                            <div className="flex flex-col px-2.5 gap-1">
                                <div className="pb-2">
                                    <InspectorPanelKeyValueDropdown
                                        id="palette"
                                        isOpen={openDropdownId === "palette"}
                                        setOpenDropdownId={setOpenDropdownId}
                                        keyTitle="Palette"
                                        Icon={Icons.Gradient}
                                        iconClasses="text-gray-0"
                                    >
                                        <OrthoPalette
                                            selectedPalette={palette}
                                            setSelectedPalette={setPalette}
                                            setOpenDropdownId={setOpenDropdownId}
                                        />
                                    </InspectorPanelKeyValueDropdown>
                                    <InspectorPanelAccordionSlider
                                        title="Opacity"
                                        resetValue={100}
                                        existingSliderValue={existingOpacity}
                                        minSliderValue={1}
                                        maxSliderValue={100}
                                        onValueChanged={(opacity) =>
                                            debounceDispatch(
                                                setOrthoOpacity({
                                                    id: flight.id,
                                                    updates: { opacity },
                                                })
                                            )
                                        }
                                    />
                                </div>
                                {!!orthoMask && (
                                    <InspectorPanelAccordionKeyValueToggle
                                        keyTitle="Crop"
                                        isToggled={orthoMask.enabled}
                                        setIsToggled={() =>
                                            dispatch(
                                                updateOrthoMask({
                                                    id: orthoMask.id,
                                                    updates: { enabled: !orthoMask.enabled },
                                                })
                                            )
                                        }
                                    />
                                )}
                            </div>
                            <div
                                className={classNames(
                                    "flex pt-2 px-2.5 w-full border-t border-t-gray-700 text-gray-50 gap-1"
                                )}
                            >
                                {orthomosaicIsEnabled && flight.visible && (
                                    <ButtonTertiary
                                        title="Compare"
                                        LeftIcon={Icons.RatioBar}
                                        className="w-full"
                                        onClick={handleActivateComparison}
                                    />
                                )}
                                <ButtonTertiary
                                    title="Crop"
                                    LeftIcon={Icons.Crop}
                                    className="w-full"
                                    onClick={() => {
                                        dispatch(setActiveTool(ToolName.OrthoMask))
                                        dispatch(
                                            startMaskDrawing({
                                                flightId: flight.id,
                                                existingMask: orthoMask,
                                            })
                                        )
                                    }}
                                />
                            </div>
                        </InspectorPanelAccordion>
                    )}
                </>
            )}
        </div>
    )
})
InspectorPanelHotspotLayer.displayName = "InspectorPanelHotspotLayer"
export { InspectorPanelHotspotLayer }
